import React, { useState } from "react";
import { Link } from "react-router-dom";
import EngineeringIcon from "./EngineeringIcons";
import AnimatedDivider from "./AnimatedDivider";

interface CompletedProject {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  client: string;
  budget: string;
  completionDate: string;
  technologies: string[];
  outcomes: string[];
  team: string[];
  specifications: {
    title: string;
    value: string;
    unit?: string;
  }[];
}

const CompletedProjects: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const completedProjects: CompletedProject[] = [
    {
      id: "1",
      title: "Automated Palm Oil Processing Plant",
      category: "Farm Machinery & Food Processing",
      description: "Complete automation of palm oil processing with advanced machinery and quality control systems.",
      image: "/palm_oil_mill.png",
      client: "Ekumfi Palm Oil Cooperative",
      budget: "GHS 184,000",
      completionDate: "August 2023",
      technologies: ["Automated Processing", "Quality Control", "IoT Monitoring", "Energy Recovery"],
      outcomes: ["50% efficiency increase", "95% waste reduction", "40% energy savings", "25 new jobs created"],
      team: ["Kwame Asante", "Sarah Mensah", "David Osei", "Grace Addo"],
      specifications: [
        { title: "Processing Capacity", value: "50", unit: "tons/day" },
        { title: "Efficiency Rate", value: "95", unit: "%" },
        { title: "Energy Savings", value: "40", unit: "%" }
      ]
    },
    {
      id: "palm-threshing-machine-installation",
      title: "Palm Threshing Machine Installation",
      category: "Farm Machinery & Food Processing",
      description: "High-efficiency palm fruit threshing machine designed for optimal fruit separation and processing.",
      image: "/palm_threshing_machine.png",
      client: "Ekumfi Palm Oil Cooperative",
      budget: "GHS 9,000",
      completionDate: "June 2023",
      technologies: ["Automated Threshing", "Quality Sensors", "Energy Management", "Safety Systems"],
      outcomes: ["40% efficiency increase", "95% fruit integrity", "30% energy reduction", "15% oil yield improvement"],
      team: ["Kwame Asante", "Sarah Mensah", "David Osei", "Grace Addo"],
      specifications: [
        { title: "Threshing Capacity", value: "2", unit: "tons/hour" },
        { title: "Fruit Integrity", value: "95", unit: "%" },
        { title: "Energy Efficiency", value: "30", unit: "% improvement" }
      ]
    },
    {
      id: "palm-kernel-oil-extraction",
      title: "Palm Kernel Oil Extraction System",
      category: "Farm Machinery & Food Processing",
      description: "Advanced palm kernel oil extraction machine with high yield rate and automated processing.",
      image: "/palm_kenel_oil_machines.png",
      client: "Ekumfi Palm Oil Cooperative",
      budget: "GHS 10,000",
      completionDate: "October 2023",
      technologies: ["Automated Extraction", "Quality Control", "Energy Management", "Filtration Systems"],
      outcomes: ["25% yield increase", "99.5% oil purity", "50% time reduction", "Full automation achieved"],
      team: ["Michael Kofi", "Abena Osei", "Kwesi Addo", "Nana Yaa"],
      specifications: [
        { title: "Extraction Capacity", value: "500", unit: "L/day" },
        { title: "Oil Yield", value: "25", unit: "% improvement" },
        { title: "Oil Purity", value: "99.5", unit: "%" }
      ]
    },
    {
      id: "interlocking-brick-factory",
      title: "Interlocking Brick Factory",
      category: "Sustainable Building",
      description: "Modern interlocking brick manufacturing facility with sustainable production methods.",
      image: "/project3.png",
      client: "Ekumfi Construction Cooperative",
      budget: "GHS 155,000",
      completionDate: "December 2023",
      technologies: ["Automated Production", "Quality Control", "Sustainable Materials", "Energy Efficiency"],
      outcomes: ["100% sustainable materials", "60% faster construction", "40% cost reduction", "Zero waste production"],
      team: ["Sarah Mensah", "Kwame Asante", "Grace Addo", "Michael Kofi"],
      specifications: [
        { title: "Production Capacity", value: "10,000", unit: "bricks/day" },
        { title: "Material Efficiency", value: "100", unit: "% sustainable" },
        { title: "Construction Speed", value: "60", unit: "% faster" }
      ]
    },
    {
      id: "solar-water-pumping",
      title: "Solar Water Pumping System",
      category: "Energy Engineering",
      description: "Large-scale solar-powered water pumping system for agricultural irrigation.",
      image: "/project4.png",
      client: "Ekumfi Agricultural Authority",
      budget: "GHS 10,000",
      completionDate: "November 2023",
      technologies: ["Solar Power", "Smart Pumping", "IoT Monitoring", "Energy Management"],
      outcomes: ["100% renewable energy", "80% water savings", "Real-time monitoring", "60% crop yield increase"],
      team: ["Michael Kofi", "Abena Osei", "David Osei", "Nana Yaa"],
      specifications: [
        { title: "Solar Capacity", value: "300", unit: "kW" },
        { title: "Water Savings", value: "80", unit: "%" },
        { title: "Energy Independence", value: "100", unit: "%" }
      ]
    },
    {
      id: "wood-processing-facility",
      title: "Wood Processing Facility",
      category: "Wood Works Engineering",
      description: "Advanced wood processing and manufacturing facility with precision cutting systems.",
      image: "/project5.png",
      client: "Ekumfi Wood Products Cooperative",
      budget: "GHS 231,000",
      completionDate: "September 2023",
      technologies: ["Precision Cutting", "Automated Assembly", "Quality Control", "Waste Management"],
      outcomes: ["400% efficiency increase", "Perfect precision cutting", "40% waste reduction", "Custom furniture production"],
      team: ["Kwame Asante", "Sarah Mensah", "Grace Addo", "Michael Kofi"],
      specifications: [
        { title: "Processing Capacity", value: "20", unit: "cubic meters/day" },
        { title: "Precision Accuracy", value: "99.9", unit: "%" },
        { title: "Waste Reduction", value: "40", unit: "%" }
      ]
    }
  ];

  const categories = [
    { id: "all", name: "All Projects", icon: "🏗️" },
    { id: "Farm Machinery & Food Processing", name: "Farm Machinery", icon: "🚜" },
    { id: "Energy Engineering", name: "Energy Engineering", icon: "⚡" },
    { id: "Sustainable Building", name: "Sustainable Building", icon: "🏗️" },
    { id: "Wood Works Engineering", name: "Wood Works", icon: "🪵" }
  ];

  const filteredProjects = completedProjects.filter(project => {
    const matchesCategory = selectedCategory === "all" || project.category === selectedCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const stats = {
    totalProjects: completedProjects.length,
    totalBudget: completedProjects.reduce((sum, project) => sum + parseFloat(project.budget.replace(/[^\d.]/g, '')), 0),
    totalJobs: completedProjects.reduce((sum, project) => sum + project.team.length, 0),
    avgEfficiency: 85
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-green-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-spin-slow">
                <EngineeringIcon type="gear" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Completed Projects</h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Showcasing our successfully delivered engineering projects that have transformed 
              industries and communities across Ekumfiman.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.totalProjects}</div>
                <div className="text-green-200">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">GHS {stats.totalBudget.toLocaleString()}</div>
                <div className="text-green-200">Total Investment</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.totalJobs}</div>
                <div className="text-green-200">Jobs Created</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.avgEfficiency}%</div>
                <div className="text-green-200">Avg. Efficiency</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Filters and Search */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative max-w-md mx-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EngineeringIcon type="wrench" size="sm" className="text-gray-400" animated={false} />
                </div>
                <input
                  type="text"
                  placeholder="Search completed projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${
                    selectedCategory === category.id
                      ? "bg-green-500 text-white shadow-lg transform scale-105"
                      : "bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700"
                  }`}
                >
                  <span className="text-lg">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {filteredProjects.length === 0 ? (
              <div className="text-center py-16">
                <EngineeringIcon type="cog" size="lg" className="text-gray-400 mx-auto mb-4" animated={false} />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No projects found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <div key={project.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 engineering-card">
                    {/* Project Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center justify-between">
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                            Completed
                          </span>
                          <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                            {project.completionDate}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      {/* Category */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🚜</span>
                        <span className="text-sm text-gray-500 font-medium">{project.category}</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                        {project.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {project.description}
                      </p>

                      {/* Key Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{project.budget}</div>
                          <div className="text-xs text-gray-500">Budget</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{project.team.length}</div>
                          <div className="text-xs text-gray-500">Team Members</div>
                        </div>
                      </div>

                      {/* Technologies */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <EngineeringIcon type="bolt" size="sm" className="text-green-500" animated={false} />
                          Technologies Used
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.slice(0, 3).map((tech, index) => (
                            <span key={index} className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs font-medium">
                              {tech}
                            </span>
                          ))}
                          {project.technologies.length > 3 && (
                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                              +{project.technologies.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Key Outcomes */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <EngineeringIcon type="chain" size="sm" className="text-green-500" animated={false} />
                          Key Outcomes
                        </h4>
                        <ul className="space-y-1">
                          {project.outcomes.slice(0, 2).map((outcome, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Specifications */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <EngineeringIcon type="screw" size="sm" className="text-green-500" animated={false} />
                          Key Specifications
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {project.specifications.slice(0, 2).map((spec, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-600">{spec.title}</span>
                              <span className="font-semibold text-gray-800">
                                {spec.value}{spec.unit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <Link
                        to={`/projects/${project.id}`}
                        className="block w-full bg-green-500 text-white text-center py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <EngineeringIcon type="gear" size="sm" animated={false} />
                        View Project Details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's add your project to our portfolio of successful engineering achievements. 
            Our team is ready to bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacts"
              className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <EngineeringIcon type="wrench" size="sm" animated={false} />
              Start Your Project
            </Link>
            <Link
              to="/projects/ongoing"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors flex items-center justify-center gap-2"
            >
              <EngineeringIcon type="gear" size="sm" />
              View Ongoing Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompletedProjects;
